/**
 * firebase/config
 * Firebase app initialization + exported SDK handles.
 *
 * Config precedence:
 * 1) `REACT_APP_FIREBASE_*` environment variables
 * 2) local fallback `src/firebaseConfig.js`
 *
 * If required keys are missing, SDK instances are left null and callers should
 * fail fast through `assertFirebaseConfigured()`.
 */
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { firebaseConfig as localFirebaseConfig } from '../../firebaseConfig';

const envFirebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const firebaseConfig = {
  apiKey: envFirebaseConfig.apiKey || localFirebaseConfig?.apiKey || '',
  authDomain:
    envFirebaseConfig.authDomain || localFirebaseConfig?.authDomain || '',
  projectId: envFirebaseConfig.projectId || localFirebaseConfig?.projectId || '',
  storageBucket:
    envFirebaseConfig.storageBucket || localFirebaseConfig?.storageBucket || '',
  messagingSenderId:
    envFirebaseConfig.messagingSenderId ||
    localFirebaseConfig?.messagingSenderId ||
    '',
  appId: envFirebaseConfig.appId || localFirebaseConfig?.appId || '',
  measurementId:
    envFirebaseConfig.measurementId || localFirebaseConfig?.measurementId || '',
};

const requiredFirebaseKeys = [
  'apiKey',
  'authDomain',
  'projectId',
  'storageBucket',
  'messagingSenderId',
  'appId',
];

const missingFirebaseConfig = requiredFirebaseKeys.filter(
  (key) => !firebaseConfig[key]
);

export const isFirebaseConfigured = missingFirebaseConfig.length === 0;

let app = null;
let auth = null;
let db = null;
let storage = null;

if (isFirebaseConfigured) {
  // Initialize once; module-level exports are shared across the app.
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
}

export function assertFirebaseConfigured() {
  if (!isFirebaseConfigured) {
    throw new Error(
      `Firebase is not configured. Missing: ${missingFirebaseConfig.join(', ')}`
    );
  }
}

export { app, auth, db, storage, firebaseConfig, missingFirebaseConfig };
